/**
 * Wrapper component for routes that require authentication.
 *
 * Redirects to home page if user is not signed in.
 * Can also require admin, moderator, or super-admin privileges.
 */
import { useAuth } from '@clerk/clerk-react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUserProfile } from '../../hooks/useUserProfile';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Require admin role (is_admin) */
  requireAdmin?: boolean;
  /** Require moderator role (is_admin OR is_moderator) */
  requireModerator?: boolean;
  /** Require super admin role (is_super_admin only) */
  requireSuperAdmin?: boolean;
}

export function ProtectedRoute({
  children,
  requireAdmin = false,
  requireModerator = false,
  requireSuperAdmin = false,
}: ProtectedRouteProps) {
  const { isSignedIn, isLoaded } = useAuth();
  const { data: profile, isLoading: profileLoading } = useUserProfile();
  const location = useLocation();

  const needsRoleCheck = requireAdmin || requireModerator || requireSuperAdmin;

  // Show loading state while checking auth.
  // Only block on profile loading when role-based checks are needed — otherwise
  // a profile query refetch (e.g. after createProfile) can unmount children and
  // destroy their local state (the onboarding step-reset bug).
  if (!isLoaded || (isSignedIn && needsRoleCheck && profileLoading)) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <svg className="animate-spin h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24">
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
    );
  }

  // Redirect to home if not signed in
  if (!isSignedIn) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Check admin requirement (super_admin also counts as admin)
  if (requireAdmin && !profile?.is_admin && !profile?.is_super_admin) {
    return <Navigate to="/" replace />;
  }

  // Check moderator requirement (admin and super_admin also count as moderator)
  if (
    requireModerator &&
    !profile?.is_admin &&
    !profile?.is_moderator &&
    !profile?.is_super_admin
  ) {
    return <Navigate to="/" replace />;
  }

  // Check super admin requirement (strictest - only is_super_admin)
  if (requireSuperAdmin && !profile?.is_super_admin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
